/**
 * Auth Store (Pinia)
 * Handles authentication state - Vue 3 compatible
 * 
 * Security: Access token is stored in memory only (not persisted to localStorage).
 * The refresh token is stored in an HttpOnly cookie by the backend.
 * On page reload, the frontend calls /api/token/refresh to obtain a new access token.
 */

import { defineStore } from 'pinia';
import router from '@/router';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null,         // Access token — memory only, NOT persisted
    refreshToken: null,  // Kept for fallback body-based refresh; HttpOnly cookie is primary
    role: null,
    is_pro: null,
    id: null,
    cartCount: 0,        // Shopping cart item count — memory only
    _isRefreshing: false // Mutex to prevent concurrent refresh attempts
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isAdmin: (state) => state.role === 'Admin',
    isVeto: (state) => state.role === 'Veto',
    isPro: (state) => state.is_pro === true,
    userId: (state) => state.id,
    userRole: (state) => state.role,
    getCartCount: (state) => state.cartCount
  },

  actions: {
    /**
     * Login user with JWT token pair
     */
    login(token, refreshToken = null) {
      this.token = token;
      if (refreshToken) {
        this.refreshToken = refreshToken;
      }
    },

    /**
     * Set user information after login
     */
    setUserInfo({ role, is_pro, id }) {
      this.role = role;
      this.is_pro = is_pro;
      this.id = id;
    },

    /**
     * Set user as veterinary
     */
    setVeto() {
      this.role = 'Veto';
    },

    /**
     * Refresh the access token using the refresh token.
     * Returns true on success, false on failure.
     */
    async refreshAccessToken() {
      if (this._isRefreshing) {
        // Wait for the ongoing refresh to complete
        return new Promise((resolve) => {
          const interval = setInterval(() => {
            if (!this._isRefreshing) {
              clearInterval(interval);
              resolve(!!this.token);
            }
          }, 100);
        });
      }
      
      this._isRefreshing = true;
      try {
        const { default: ApiService } = await import('@/services/api');
        const response = await ApiService.post('/api/token/refresh', {
          refresh_token: this.refreshToken || ''
        });
        
        if (response.data && response.data.token) {
          this.token = response.data.token;
          if (response.data.refresh_token) {
            this.refreshToken = response.data.refresh_token;
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
      } finally {
        this._isRefreshing = false;
      }
    },

    /**
     * Attempt to restore session on page reload by refreshing the token.
     * Called from router beforeEach or app initialization.
     */
    async restoreSession() {
      // If we have persisted auth state (role, id) but no token, try refreshing
      if (!this.token && (this.role || this.id || this.refreshToken)) {
        const success = await this.refreshAccessToken();
        if (!success) {
          this.clearState();
        } else {
          // Token restored — refresh cart count from backend
          this.fetchCartCount();
        }
        return success;
      }
      return !!this.token;
    },

    /**
     * Logout user and redirect to login.
     * Revokes the refresh token on the backend before clearing state.
     */
    async logout() {
      try {
        const { default: ApiService } = await import('@/services/api');
        await ApiService.post('/api/logout', {
          refresh_token: this.refreshToken || ''
        });
      } catch (error) {
        // Logout API call failed — clear state anyway
        console.warn('Logout API call failed:', error);
      }
      
      this.clearState();
      router.push({ path: '/connexion' });
    },

    /**
     * Clear all auth state without calling the API
     */
    clearState() {
      this.token = null;
      this.refreshToken = null;
      this.role = null;
      this.is_pro = null;
      this.id = null;
      this.cartCount = 0;
    },

    /**
     * Check if token is expired
     */
    isTokenExpired() {
      if (!this.token) return true;
      
      try {
        const payload = this.token.split('.')[1];
        const decoded = JSON.parse(atob(payload));
        const exp = decoded.exp * 1000; // Convert to milliseconds
        return Date.now() >= exp;
      } catch (error) {
        console.error('Error decoding token:', error);
        return true;
      }
    },

    /**
     * Get decoded token payload
     */
    getDecodedToken() {
      if (!this.token) return null;
      
      try {
        const payload = this.token.split('.')[1];
        return JSON.parse(atob(payload));
      } catch (error) {
        console.error('Error decoding token:', error);
        return null;
      }
    },

    async fetchCartCount() {
      if (this.role !== 'Default') {
        this.cartCount = 0;
        return;
      }
      if (!this.token) {
        // Token not yet available (e.g. page reload before session restore).
        // Keep the cached cartCount — it will be refreshed once the token is restored.
        return;
      }
      try {
        const { default: ApiService } = await import('@/services/api');
        const response = await ApiService.get('api/users/me/cart/');
        this.cartCount = response.data ? response.data.length : 0;
      } catch {
        // Keep the cached count on transient errors
      }
    },

    setCartCount(count) {
      this.cartCount = count;
    }
  },

  // Persist role, is_pro, id, refreshToken to localStorage.
  // Access token is NOT persisted (memory only) for security.
  persist: {
    key: 'auth',
    storage: localStorage,
    paths: ['role', 'is_pro', 'id', 'refreshToken', 'cartCount']
  }
});
